import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { invoiceService } from '../services/invoiceService';
import { InvoicePayments } from '../components/InvoicePayments';
import PdfViewer from '../components/PdfViewer';
import CorrectionInvoiceModal from '../components/CorrectionInvoiceModal';
import CorrectionInvoiceList from '../components/CorrectionInvoiceList';
import EditInvoiceModal from '../components/EditInvoiceModal';
import { useTranslation } from '../hooks/useTranslation';

const InvoiceDetails: React.FC = () => {
  const { companyId, invoiceId } = useParams<{ companyId: string; invoiceId: string }>();
  const [activeTab, setActiveTab] = useState<'details' | 'payments' | 'corrections'>('details');
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { t } = useTranslation();

  const { data: invoice, isLoading, error, refetch } = useQuery({
    queryKey: ['invoice', companyId, invoiceId],
    queryFn: () => invoiceService.getInvoice(companyId!, invoiceId!),
    enabled: !!companyId && !!invoiceId
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t('common.error')}</p>
        <Link to={`/companies/${companyId}`} className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          {t('companies.backToList')}
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'details', label: t('companies.invoices.details'), icon: '📄' },
    { id: 'payments', label: t('transactions.title'), icon: '💰' },
    { id: 'corrections', label: t('correctionInvoices.title') || 'Corrections', icon: '🔧' }
  ] as const;

  const formatCurrency = (amount: number, currency: string) => {
    const locale = navigator.language || 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'SENT': return 'bg-blue-100 text-blue-800';
      case 'PARTIALLY_PAID': return 'bg-yellow-100 text-yellow-800';
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return t(`invoices.statuses.${status}`) || status;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Заголовок */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
              <Link to="/companies" className="hover:text-gray-700">{t('nav.companies')}</Link>
              <span>›</span>
              <Link to={`/companies/${companyId}`} className="hover:text-gray-700">
                {invoice.issuerName}
              </Link>
              <span>›</span>
              <span className="text-gray-900">{t('invoices.title')} {invoice.invoiceNumber}</span>
            </nav>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('invoices.title')} {invoice.invoiceNumber}
            </h1>
          </div>
          
          <div className="flex items-center space-x-3">
            <Link
              to={`/companies/${companyId}#invoices`}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              {t('companies.backToList')}
            </Link>
            <button
              onClick={() => setCorrectionModalOpen(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
              {t('correctionInvoices.createCorrection')}
            </button>
            <button
              onClick={() => setPdfViewerOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('invoices.viewPdf')}
            </button>
            <button
              onClick={() => setEditModalOpen(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              {t('common.edit')}
            </button>
          </div>
        </div>
      </div>

      {/* Вкладки */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Контент вкладок */}
      <div className="min-h-96">
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Основная информация */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">{t('invoices.basicInfo') || t('companies.invoices.details')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">{t('invoices.seller') || 'Seller'}</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>{t('companies.name')}:</strong> {invoice.issuerName}</p>
                    {invoice.issuerTaxId && <p><strong>{t('companies.taxId')}:</strong> {invoice.issuerTaxId}</p>}
                    {invoice.issuerAddress && <p><strong>{t('companies.address')}:</strong> {invoice.issuerAddress}</p>}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">{t('invoices.buyer') || 'Buyer'}</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>{t('companies.name')}:</strong> {invoice.clientName}</p>
                    {invoice.clientTaxId && <p><strong>{t('companies.taxId')}:</strong> {invoice.clientTaxId}</p>}
                    {invoice.clientAddress && <p><strong>{t('companies.address')}:</strong> {invoice.clientAddress}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Детали инвойса */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">{t('companies.invoices.details')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-600">{t('invoices.invoiceNumber')}</p>
                  <p className="font-medium">{invoice.invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('invoices.issueDate')}</p>
                  <p className="font-medium">{new Date(invoice.issueDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('invoices.dueDate')}</p>
                  <p className="font-medium">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : t('common.notSpecified') || 'Not specified'}</p>
                </div>
                {invoice.saleDate && (
                  <div>
                    <p className="text-sm text-gray-600">{t('invoices.saleDate')}</p>
                    <p className="font-medium">{new Date(invoice.saleDate).toLocaleDateString()}</p>
                  </div>
                )}
                {invoice.placeOfIssue && (
                  <div>
                    <p className="text-sm text-gray-600">{t('invoices.placeOfIssue')}</p>
                    <p className="font-medium">{invoice.placeOfIssue}</p>
                  </div>
                )}
                {invoice.invoiceType && (
                  <div>
                    <p className="text-sm text-gray-600">{t('invoices.invoiceType')}</p>
                    <p className="font-medium">{t(`invoices.types.${invoice.invoiceType}`) || invoice.invoiceType}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">{t('invoices.status')}</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {getStatusLabel(invoice.status)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('invoices.currency')}</p>
                  <p className="font-medium">{t(`invoices.currencies.${invoice.currency}`) || invoice.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('invoices.grandTotal')}</p>
                  <p className="font-medium text-lg">{formatCurrency(invoice.totalAmount, invoice.currency)}</p>
                </div>
                {invoice.currency !== 'PLN' && 
                 invoice.taxAmountInPln != null && 
                 typeof invoice.taxAmountInPln === 'number' && 
                 invoice.taxAmountInPln > 0 && (
                  <div>
                    <p className="text-sm text-gray-600">{t('invoices.exchangeRates.vatInPln')}</p>
                    <p className="font-medium text-lg">{formatCurrency(invoice.taxAmountInPln, 'PLN')}</p>
                    <p className="text-xs text-gray-500">{t('invoices.exchangeRates.forDate') || 'At previous day rate'}</p>
                  </div>
                )}
              </div>
              
              {/* Курсы валют и дополнительные поля */}
              {(invoice.exchangeRate || invoice.exchangeRateTable || invoice.exchangeRateDate || invoice.reverseCharge) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-md font-medium mb-4">{t('invoices.additionalInfo') || 'Additional Information'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {invoice.exchangeRate && (
                      <div>
                        <p className="text-sm text-gray-600">{t('invoices.exchangeRate')}</p>
                        <p className="font-medium">{invoice.exchangeRate} {invoice.currency}/PLN</p>
                      </div>
                    )}
                    {invoice.exchangeRateTable && (
                      <div>
                        <p className="text-sm text-gray-600">{t('invoices.exchangeRateTable')}</p>
                        <p className="font-medium">{invoice.exchangeRateTable}</p>
                      </div>
                    )}
                    {invoice.exchangeRateDate && (
                      <div>
                        <p className="text-sm text-gray-600">{t('invoices.exchangeRateDate')}</p>
                        <p className="font-medium">{invoice.exchangeRateDate}</p>
                      </div>
                    )}
                    {invoice.reverseCharge && (
                      <div>
                        <p className="text-sm text-gray-600">{t('invoices.reverseCharge')}</p>
                        <p className="font-medium text-green-600">{t('common.yes') || 'Yes'}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Позиции инвойса */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">{t('invoices.items')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.description')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.unitOfMeasure')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.quantity')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.unitPrice')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoice.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.unitOfMeasure ? t(`invoices.unitOfMeasures.${item.unitOfMeasure}`) || item.unitOfMeasure : t('invoices.unitOfMeasures.PIECE')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.unitPrice, invoice.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.totalPrice, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Примечания */}
            {invoice.notes && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">{t('invoices.notes')}</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <InvoicePayments
            companyId={companyId!}
            invoiceId={invoiceId!}
            invoiceNumber={invoice.invoiceNumber}
            totalAmount={invoice.totalAmount}
            currency={invoice.currency}
          />
        )}

        {activeTab === 'corrections' && (
          <CorrectionInvoiceList
            key={refreshKey}
            companyId={companyId!}
            originalInvoiceId={invoiceId!}
          />
        )}
      </div>

      {/* Модальное окно просмотра PDF */}
      {invoice && (
        <PdfViewer
          companyId={companyId!}
          invoiceId={invoiceId!}
          invoiceNumber={invoice.invoiceNumber}
          clientName={invoice.clientName}
          pdfLanguage={invoice.pdfLanguage}
          isOpen={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
        />
      )}

      {/* Модальное окно создания корректировки */}
      <CorrectionInvoiceModal
        isOpen={correctionModalOpen}
        onClose={() => setCorrectionModalOpen(false)}
        onSuccess={() => {
          setRefreshKey(prev => prev + 1);
          setCorrectionModalOpen(false);
        }}
        companyId={companyId!}
        originalInvoiceId={invoiceId!}
      />

      {/* Модальное окно редактирования инвойса */}
      {invoice && (
        <EditInvoiceModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          invoice={invoice}
          companyId={companyId!}
          onSuccess={() => {
            refetch();
            setEditModalOpen(false);
          }}
          onInvoiceUpdated={() => {
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default InvoiceDetails;
